/**
 * Hook React pour la gestion des mots de passe
 * RÉUTILISABILITÉ : 100% - Compatible Next.js et React
 *
 * Ce hook encapsule la logique de gestion des mots de passe :
 * - Validation de la force du mot de passe
 * - Changement de mot de passe
 * - Réinitialisation de mot de passe
 * - Vérification des critères de sécurité
 */

  // Client Component pour Next.js

import { useState, useMemo } from 'react';
import { useAuthStore } from '../store/authStore';
import { calculatePasswordStrength, validators } from '../../infrastructure/services/validation';
import type { ChangePasswordData } from '../../application/dto/dtos';
import type { PasswordStrengthDetails } from '../../application/dto/ui.types';

/**
 * Interface du hook usePassword
 */
export interface UsePasswordReturn {
  // État
  isLoading: boolean;
  error: string | null;

  // Actions
  changePassword: (data: ChangePasswordData) => Promise<void>;

  // Utilitaires
  clearError: () => void;
}

/**
 * Hook pour la gestion du mot de passe
 *
 * @example
 * ```tsx
 * function ChangePasswordForm() {
 *   const { changePassword, isLoading, error } = usePassword();
 *
 *   const handleSubmit = async (data: ChangePasswordData) => {
 *     try {
 *       await changePassword(data);
 *       // Succès
 *     } catch (err) {
 *       // Erreur déjà gérée
 *     }
 *   };
 *
 *   return (
 *     <form onSubmit={handleSubmit}>
 *       {error && <div>{error}</div>}
 *       // ... autres champs du formulaire
 *     </form>
 *   );
 * }
 * ```
 */
export function usePassword(): UsePasswordReturn {
  const authStore = useAuthStore();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Changer le mot de passe
   */
  const changePassword = async (data: ChangePasswordData): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      await authStore.changePassword(data);
      setIsLoading(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur de changement de mot de passe';
      setError(message);
      setIsLoading(false);
      throw err;
    }
  };

  /**
   * Effacer l'erreur
   */
  const clearError = () => {
    setError(null);
  };

  return {
    // État
    isLoading,
    error,

    // Actions
    changePassword,

    // Utilitaires
    clearError,
  };
}

/**
 * Hook pour valider la force d'un mot de passe en temps réel
 *
 * @param password - Mot de passe à valider
 *
 * @example
 * ```tsx
 * function PasswordInput() {
 *   const [password, setPassword] = useState('');
 *   const strength = usePasswordStrength(password);
 *
 *   return (
 *     <div>
 *       <input
 *         type="password"
 *         value={password}
 *         onChange={(e) => setPassword(e.target.value)}
 *       />
 *       <PasswordStrengthIndicator strength={strength} />
 *     </div>
 *   );
 * }
 * ```
 */
export function usePasswordStrength(password: string): PasswordStrengthDetails {
  return useMemo(() => {
    if (!password) {
      return {
        score: 0,
        label: 'weak',
        suggestions: ['Le mot de passe est requis'],
        hasMinLength: false,
        hasUppercase: false,
        hasLowercase: false,
        hasNumber: false,
        hasSpecialChar: false,
      };
    }

    return calculatePasswordStrength(password);
  }, [password]);
}

/**
 * Hook pour valider si un mot de passe respecte les critères minimum
 *
 * @param password - Mot de passe à valider
 *
 * @example
 * ```tsx
 * function SignupForm() {
 *   const [password, setPassword] = useState('');
 *   const isValid = usePasswordValidation(password);
 *
 *   return (
 *     <div>
 *       <input
 *         type="password"
 *         value={password}
 *         onChange={(e) => setPassword(e.target.value)}
 *       />
 *       <button disabled={!isValid}>S'inscrire</button>
 *     </div>
 *   );
 * }
 * ```
 */
export function usePasswordValidation(password: string): boolean {
  return useMemo(() => {
    if (!password) return false;

    const validationError = validators.password(password);
    return validationError === null;
  }, [password]);
}

/**
 * Hook pour comparer deux mots de passe (confirmation)
 *
 * @param password - Mot de passe principal
 * @param confirmPassword - Confirmation du mot de passe
 *
 * @example
 * ```tsx
 * function PasswordFields() {
 *   const [password, setPassword] = useState('');
 *   const [confirmPassword, setConfirmPassword] = useState('');
 *   const passwordsMatch = usePasswordMatch(password, confirmPassword);
 *
 *   return (
 *     <div>
 *       <input
 *         type="password"
 *         value={password}
 *         onChange={(e) => setPassword(e.target.value)}
 *         placeholder="Mot de passe"
 *       />
 *       <input
 *         type="password"
 *         value={confirmPassword}
 *         onChange={(e) => setConfirmPassword(e.target.value)}
 *         placeholder="Confirmer le mot de passe"
 *       />
 *       {!passwordsMatch && confirmPassword && (
 *         <div>Les mots de passe ne correspondent pas</div>
 *       )}
 *     </div>
 *   );
 * }
 * ```
 */
export function usePasswordMatch(password: string, confirmPassword: string): boolean {
  return useMemo(() => {
    if (!password || !confirmPassword) return false;
    return password === confirmPassword;
  }, [password, confirmPassword]);
}

/**
 * Hook pour obtenir les suggestions d'amélioration du mot de passe
 *
 * @param password - Mot de passe à analyser
 *
 * @example
 * ```tsx
 * function PasswordSuggestions() {
 *   const [password, setPassword] = useState('');
 *   const suggestions = usePasswordSuggestions(password);
 *
 *   return (
 *     <div>
 *       <input
 *         type="password"
 *         value={password}
 *         onChange={(e) => setPassword(e.target.value)}
 *       />
 *       {suggestions.length > 0 && (
 *         <ul>
 *           {suggestions.map((suggestion, i) => (
 *             <li key={i}>{suggestion}</li>
 *           ))}
 *         </ul>
 *       )}
 *     </div>
 *   );
 * }
 * ```
 */
export function usePasswordSuggestions(password: string): string[] {
  return useMemo(() => {
    if (!password) {
      return ['Le mot de passe est requis'];
    }

    const strength = calculatePasswordStrength(password);
    return strength.suggestions;
  }, [password]);
}

/**
 * Hook pour obtenir la couleur associée à la force du mot de passe
 * Utile pour les indicateurs visuels
 *
 * @param password - Mot de passe à analyser
 *
 * @example
 * ```tsx
 * function PasswordStrengthBar() {
 *   const [password, setPassword] = useState('');
 *   const color = usePasswordStrengthColor(password);
 *
 *   return (
 *     <div>
 *       <input
 *         type="password"
 *         value={password}
 *         onChange={(e) => setPassword(e.target.value)}
 *       />
 *       <div
 *         className="strength-bar"
 *         style={{ backgroundColor: color }}
 *       />
 *     </div>
 *   );
 * }
 * ```
 */
export function usePasswordStrengthColor(password: string): string {
  return useMemo(() => {
    if (!password) return '#e0e0e0';

    const strength = calculatePasswordStrength(password);

    const colorMap: Record<string, string> = {
      'weak': '#f44336',        // Rouge
      'fair': '#ff9800',        // Orange
      'good': '#ffeb3b',        // Jaune
      'strong': '#8bc34a',      // Vert clair
      'very-strong': '#4caf50', // Vert foncé
    };

    return colorMap[strength.label] || '#e0e0e0';
  }, [password]);
}
